<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="remote-field-trips-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <h2>Introduction</h2>
            <p>
              Geography and science lessons often struggle to capture students'
              attention when confined to textbooks and static images. Remote
              field trips, delivered through live drone streams, offer a
              transformative alternative. By bringing real-time aerial views of
              far-flung landscapes directly into the classroom, educators can
              turn abstract concepts into vivid, shared experiences that spark
              curiosity and sustain focus.
            </p>
          </section>

          <section class="blog-section">
            <h2>Why Remote Field Trips Matter</h2>
            <p>
              Traditional excursions require buses, chaperones, permissions and
              significant budgets—often limiting schools to nearby parks or
              local sites. With live drone tours, those barriers disappear.
              Students gain access to wildfire-scarred canyons in California,
              remote coral reefs in the Caribbean, or ancient terraces in the
              Andes. Seeing real landscapes in motion helps learners connect
              textbook theory with tangible examples, making lessons more
              memorable and meaningful.
            </p>
          </section>

          <section class="blog-section">
            <h2>Engaging Students Through Live Interaction</h2>
            <p>
              What sets remote field trips apart is interactivity. During a live
              stream, students can request specific camera angles, ask the pilot
              questions, and direct the flight path. This two-way dialogue
              transforms them from passive observers into active participants.
              When a student points out a meandering river bend or wonders about
              a desert oasis, the pilot responds in real time, deepening
              comprehension and encouraging further inquiry.
            </p>
          </section>

          <section class="blog-section">
            <h2>Simplifying Logistics and Maximizing Classroom Time</h2>
            <p>
              Coordinating an offsite trip can eat into valuable instructional
              hours. In contrast, a remote field trip takes only a few clicks to
              schedule and join. Teachers receive a secure link, prepare a brief
              map or vocabulary preview, and then launch the session at the
              appointed time. There's no lost lesson time in travel, no
              weather-related cancellations, and no safety concerns beyond
              reliable internet—so more of your class period is devoted to
              exploration and discussion.
            </p>
          </section>

          <section class="blog-section">
            <h2>Seamless Curriculum Integration</h2>
            <p>
              Remote field trips can be tailored to align with any syllabus or
              set of learning standards. Whether the goal is understanding plate
              tectonics, coastal erosion, urban development, or biodiversity
              hotspots, each tour comes with a concise guide linking flight
              segments to specific objectives. This ensures that every minute of
              aerial footage reinforces the day's lesson and provides clear,
              measurable outcomes for assessment.
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Conclusion</h2>
            <div class="table-container">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>Aspect</th>
                    <th>Traditional Field Trip</th>
                    <th>Remote Drone Field Trip</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Cost & Logistics</td>
                    <td>High (transport, permissions, chaperones)</td>
                    <td>Low (online link, minimal prep)</td>
                  </tr>
                  <tr>
                    <td>Access to Locations</td>
                    <td>Local or nearby sites only</td>
                    <td>Global — volcanoes, reefs, historic sites</td>
                  </tr>
                  <tr>
                    <td>Interactivity</td>
                    <td>Observation, occasional Q&A</td>
                    <td>Live Q&A, student-directed camera angles</td>
                  </tr>
                  <tr>
                    <td>Time Efficiency</td>
                    <td>Travel eats into class time</td>
                    <td>Entire session within scheduled lesson block</td>
                  </tr>
                  <tr>
                    <td>Curriculum Alignment</td>
                    <td>Post-trip debrief required</td>
                    <td>Standards-mapped segments and discussion prompts</td>
                  </tr>
                  <tr>
                    <td>Weather Dependence</td>
                    <td>Easily canceled by rain or wind</td>
                    <td>Indoor, unaffected—just reliable internet needed</td>
                  </tr>
                  <tr>
                    <td>Student Engagement</td>
                    <td>Variable; passive listening</td>
                    <td>High; active exploration and decision-making</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              By leveraging live drone technology, remote field trips offer
              educators a powerful tool for engaging students in active,
              standards-aligned learning. They expand classroom walls, invite
              real-time exploration, and simplify the practical challenges of
              traditional fieldwork. For teachers seeking fresh ways to inspire
              curiosity and deepen understanding, remote field trips represent a
              new edge in student engagement—one that puts the whole world
              within reach.
            </p>
            <p>
              If you're ready to take your classroom beyond its physical limits,
              explore our educational tour offerings at
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "RemoteFieldTripsBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "remote-field-trips";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.remote-field-trips-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.table-container {
  overflow-x: auto;
  margin: 2rem 0;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.comparison-table th {
  background-color: #f8f9fa;
  text-align: left;
  padding: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #eee;
}

.comparison-table td {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.comparison-table tr:hover {
  background-color: #f8f9fa;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
